import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, Clock, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getModuleIcon, getModuleIconColor } from '@/lib/moduleIcons';
import {
  getCourseWithCurriculum,
  createSection,
  updateSection,
  deleteSection,
  lmsGetMeLearner,
  lmsGetEnrollmentStatus,
  lmsGetProgress,
  lmsGetLearnerXp,
  getTutorAssignedCourses,
  type CourseWithCurriculum,
  type CourseSection,
  type CreateSectionPayload,
  type UpdateSectionPayload,
  type LmsEnrollmentStatus,
  type LmsProgress,
} from '@/lib/api';
import { getCourseDetails } from '@/data/courseCopy';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { CourseDetailSkeleton } from '@/components/skeletons/CourseDetailSkeleton';
import { SlideOver } from '@/components/SlideOver';
import { ModuleForm } from '@/components/admin/ModuleForm';
import { EnrollModal } from '@/components/EnrollModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';

/** Badge style classes for module cards (lessons, duration) — colourful, no top bar */
const MODULE_BADGE_STYLES = [
  { lessons: 'bg-africa-blue/10 text-africa-blue border-africa-blue/20', duration: 'bg-africa-green/10 text-africa-green border-africa-green/20' },
  { lessons: 'bg-africa-green/10 text-africa-green border-africa-green/20', duration: 'bg-africa-purple/10 text-africa-purple border-africa-purple/20' },
  { lessons: 'bg-africa-purple/10 text-africa-purple border-africa-purple/20', duration: 'bg-africa-orange/10 text-africa-orange border-africa-orange/20' },
  { lessons: 'bg-africa-orange/10 text-africa-orange border-africa-orange/20', duration: 'bg-africa-red/10 text-africa-red border-africa-red/20' },
  { lessons: 'bg-africa-red/10 text-africa-red border-africa-red/20', duration: 'bg-africa-yellow/10 text-africa-yellow border-africa-yellow/20' },
  { lessons: 'bg-africa-yellow/10 text-africa-yellow border-africa-yellow/20', duration: 'bg-africa-blue/10 text-africa-blue border-africa-blue/20' },
] as const;

const PENDING_ENROLL_KEY = 'homeland_pending_enroll_course';


export function CourseDetailPage() {
  const { formatKesPrice } = useCurrency();
  const { programSlug, courseSlug } = useParams<{ programSlug: string; courseSlug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [course, setCourse] = useState<CourseWithCurriculum | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, login } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isTutor = user?.role === 'tutor';
  const [tutorAssignedCourseIds, setTutorAssignedCourseIds] = useState<Set<string>>(new Set());
  const [tutorAssignedProgramSlugs, setTutorAssignedProgramSlugs] = useState<Set<string>>(new Set());
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [modulePanelOpen, setModulePanelOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<CourseSection | null>(null);
  const [sectionToDelete, setSectionToDelete] = useState<CourseSection | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [enrollmentStatus, setEnrollmentStatus] = useState<LmsEnrollmentStatus | null>(null);
  const [progress, setProgress] = useState<LmsProgress | null>(null);
  const [courseXp, setCourseXp] = useState<number>(0);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);

  const getLearnerId = useCallback(async () => (await lmsGetMeLearner()).learnerId, []);

  const refetchEnrollment = useCallback(async () => {
    if (!user || isAdmin || !course) return;
    setEnrollmentLoading(true);
    try {
      const { learnerId: lid } = await lmsGetMeLearner();
      if (!lid) return;
      const status = await lmsGetEnrollmentStatus(lid, course.id);
      setEnrollmentStatus(status);
      if (status.enrolled && status.enrollment) {
        const [prog, xpRes] = await Promise.all([
          lmsGetProgress(status.enrollment.id),
          lmsGetLearnerXp(lid),
        ]);
        setProgress(prog);
        setCourseXp(xpRes?.totalXp ?? 0);
      } else {
        setProgress(null);
        setCourseXp(0);
      }
    } catch {
      setEnrollmentStatus(null);
      setProgress(null);
      setCourseXp(0);
    } finally {
      setEnrollmentLoading(false);
    }
  }, [user, isAdmin, course]);

  const refetchCourse = () => {
    if (!programSlug || !courseSlug) return;
    getCourseWithCurriculum(programSlug, courseSlug).then(setCourse).catch(() => {});
  };

  useEffect(() => {
    if (!programSlug || !courseSlug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCourseWithCurriculum(programSlug, courseSlug)
      .then((c) => { if (!cancelled) setCourse(c); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load course'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [programSlug, courseSlug]);

  useEffect(() => {
    if (!isTutor) return;
    getTutorAssignedCourses()
      .then((data) => {
        setTutorAssignedCourseIds(new Set((data.courses ?? []).map((c) => c.courseId)));
        setTutorAssignedProgramSlugs(new Set((data.programs ?? []).map((p) => p.programSlug)));
      })
      .catch(() => {
        setTutorAssignedCourseIds(new Set());
        setTutorAssignedProgramSlugs(new Set());
      });
  }, [isTutor]);

  const canEditCourse = Boolean(
    course &&
      (isAdmin ||
        (isTutor &&
          (tutorAssignedCourseIds.has(course.id) || tutorAssignedProgramSlugs.has(course.program_slug))))
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!course) return;
    const openEnrollOnlyForStudents = !isAdmin && !isTutor;
    const pending = window.sessionStorage.getItem(PENDING_ENROLL_KEY);
    if (openEnrollOnlyForStudents && pending && pending === course.id) {
      setEnrollModalOpen(true);
      window.sessionStorage.removeItem(PENDING_ENROLL_KEY);
    }
    if (openEnrollOnlyForStudents && searchParams.get('enroll') === '1') {
      setEnrollModalOpen(true);
    }
  }, [course, searchParams, isAdmin, isTutor]);

  useEffect(() => {
    if (!user || isAdmin || !course) {
      setEnrollmentStatus(null);
      setProgress(null);
      setCourseXp(0);
      setEnrollmentLoading(false);
      return;
    }
    let cancelled = false;
    setEnrollmentLoading(true);
    lmsGetMeLearner()
      .then(({ learnerId: lid }) => {
        if (cancelled || lid == null) return null;
        return lmsGetEnrollmentStatus(lid, course.id).then((status) => ({ status, lid }));
      })
      .then((data) => {
        if (cancelled || !data) return null;
        const { status, lid } = data;
        setEnrollmentStatus(status ?? null);
        if (status?.enrolled && status.enrollment) {
          return Promise.all([
            lmsGetProgress(status.enrollment.id),
            lmsGetLearnerXp(lid),
          ]).then(([prog, xpRes]) => {
            if (!cancelled) {
              setProgress(prog);
              setCourseXp(xpRes?.totalXp ?? 0);
            }
            return null;
          });
        }
        setProgress(null);
        setCourseXp(0);
        return null;
      })
      .catch(() => {
        if (!cancelled) {
          setEnrollmentStatus(null);
          setProgress(null);
          setCourseXp(0);
        }
      })
      .finally(() => {
        if (!cancelled) setEnrollmentLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, isAdmin, course?.id]);

  if (!programSlug || !courseSlug) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <p className="text-destructive">Invalid course.</p>
        <Link to="/courses" className="text-accent hover:underline mt-2 inline-block">Back to courses</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <CourseDetailSkeleton />
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <p className="text-destructive">{error ?? 'Course not found.'}</p>
        <Link to="/courses" className="text-accent hover:underline mt-2 inline-block">Back to courses</Link>
      </div>
    );
  }

  const detailsFromApi = course.details && course.details.trim();
  const areasFromApi = course.key_learning_areas && Array.isArray(course.key_learning_areas) && course.key_learning_areas.length > 0;
  const fallback = programSlug && courseSlug ? getCourseDetails(programSlug, courseSlug) : null;
  const details = detailsFromApi || fallback?.details || '';
  const areas = areasFromApi ? course.key_learning_areas! : (fallback?.keyLearningAreas ?? []);

  return (
    <div className="relative overflow-hidden">
      {/* Hero: header, details (no heading), badges, then learning areas */}
      <section className="pt-12 md:pt-16 pb-6 md:pb-8 px-6 relative">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-0 left-0 w-80 h-80 rounded-full bg-africa-green/10 blur-[100px]" />
          <div className="absolute bottom-0 right-10 w-64 h-64 rounded-full bg-africa-blue/5 blur-[80px]" />
        </div>
        <div className="max-w-6xl mx-auto relative">
          <button
            type="button"
            onClick={() => navigate(isTutor ? '/tutor/dashboard' : `/courses/${course.program_slug}`)}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> {isTutor ? 'Back to dashboard' : `Back to ${course.program_title}`}
          </button>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Course info: title, details content, badges, learning areas */}
            <div className="md:col-span-2">
              {course.category_name && (
                <div className="flex gap-2 mb-4">
                  <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-africa-green/10 text-africa-green border border-africa-green/20">
                    {course.category_name}
                  </span>
                </div>
              )}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
                {course.title}
              </h1>
              {details ? (
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed max-w-3xl">
                  {details}
                </p>
              ) : (
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  Part of the <Link to={`/courses/${course.program_slug}`} className="text-accent hover:underline">{course.program_title}</Link> program—hands-on learning to build real skills.
                </p>
              )}

              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground mb-6">
                {course.duration && (
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" /> {course.duration}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <BookOpen className="w-4 h-4" /> Course
                </span>
                <span className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-accent" /> Part of {course.program_title}
                </span>
              </div>

              {areas.length > 0 && (
                <div>
                  <h2 className="text-xl font-display font-bold text-foreground mb-2">Key learning areas</h2>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-3xl">
                    {areas.map((area, i) => (
                      <li key={i} className="flex items-start gap-2 text-muted-foreground text-sm">
                        <span className="mt-1 shrink-0 w-4 h-4 rounded-full bg-africa-green/20 flex items-center justify-center">
                          <CheckCircle2 className="w-2.5 h-2.5 text-africa-green" />
                        </span>
                        <span className="leading-snug">{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Sidebar card — blurred for admin/tutor when not enrolled (role cannot enroll as student) */}
            <div
              className={`bg-card border border-border rounded-2xl p-6 space-y-4 h-fit transition-all ${
                (isAdmin || isTutor) && !enrollmentStatus?.enrolled ? 'blur-sm pointer-events-none select-none' : ''
              }`}
            >
              {enrollmentStatus?.enrolled ? (
                <>
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-foreground">Your progress</h3>
                    {progress && progress.totalLessons > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                          <span>Lessons</span>
                          <span>{progress.completedCount} of {progress.totalLessons}</span>
                        </div>
                        <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-accent transition-all duration-500"
                            style={{ width: `${Math.min(100, (100 * progress.completedCount) / progress.totalLessons)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                        <span>XP</span>
                        <span>{courseXp} XP</span>
                      </div>
                      <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-africa-yellow transition-all duration-500"
                          style={{ width: `${Math.min(100, courseXp)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Earn more by completing lessons</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center">
                  <p className="text-3xl font-display font-bold text-foreground">{formatKesPrice(course.price_kes)}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isAdmin ? 'Enquire for enrolment' : 'Enroll to access all modules'}
                  </p>
                </div>
              )}
              {isAdmin ? (
                <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-gold-light rounded-xl h-12 text-base font-semibold gap-2">
                  <Link to="/contact">Enquire or enrol <ArrowRight className="w-4 h-4" /></Link>
                </Button>
              ) : enrollmentStatus?.enrolled ? (
                course.sections && course.sections.length > 0 ? (
                  <Button
                    asChild
                    className="w-full bg-accent text-accent-foreground hover:bg-gold-light rounded-xl h-12 text-base font-semibold gap-2"
                  >
                    <Link to={`/courses/${programSlug}/courses/${courseSlug}/sections/${course.sections[0].id}`}>
                      {progress && progress.completedCount > 0 ? 'Continue' : 'Start learning'}{' '}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-2">You&apos;re enrolled. Content coming soon.</p>
                )
              ) : (
                <Button
                  className="w-full bg-accent text-accent-foreground hover:bg-gold-light rounded-xl h-12 text-base font-semibold gap-2"
                  onClick={() => {
                    if (!user) {
                      toast.info('Sign in to enroll and access all lessons', { duration: 3500 });
                      if (typeof window !== 'undefined' && course) {
                        window.sessionStorage.setItem(PENDING_ENROLL_KEY, course.id);
                      }
                      login();
                      return;
                    }
                    setEnrollModalOpen(true);
                  }}
                  disabled={enrollmentLoading}
                >
                  Enroll <ArrowRight className="w-4 h-4" />
                </Button>
              )}
              {!enrollmentStatus?.enrolled && !isAdmin && (
                <p className="text-xs text-muted-foreground text-center">Get in touch for dates and availability</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Course Modules — card design from sandbox (coursedetailes) */}
      <section className="pt-6 pb-12 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-display font-bold text-foreground mb-1">Course Modules</h2>
              <p className="text-muted-foreground text-sm">
                {enrollmentStatus?.enrolled
                  ? 'Select a module to continue your progress'
                  : 'Select a module to start learning'}
              </p>
            </div>
            {canEditCourse && (
              <Button size="sm" className="bg-accent text-accent-foreground hover:bg-gold-light gap-1 shrink-0" onClick={() => { setEditingSection(null); setModulePanelOpen(true); }}>
                <Plus className="w-4 h-4" /> Add module
              </Button>
            )}
          </div>
          {course.sections && course.sections.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {course.sections.map((section, index) => {
                const ModuleIcon = getModuleIcon(section.icon);
                const iconColorClass = getModuleIconColor(section.icon);
                const badges = MODULE_BADGE_STYLES[index % MODULE_BADGE_STYLES.length];
                const lessonCount = section.lessons?.length ?? 0;
                return (
                  <div
                    key={section.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (canEditCourse) {
                        navigate(`/courses/${programSlug}/courses/${courseSlug}/sections/${section.id}`);
                        return;
                      }
                      if (!user) {
                        toast.info('Sign in to access lessons and track your progress', { duration: 4000 });
                        if (typeof window !== 'undefined' && course) {
                          window.sessionStorage.setItem(PENDING_ENROLL_KEY, course.id);
                        }
                        login();
                        return;
                      }
                      if (!enrollmentStatus?.enrolled) {
                        toast.info('Enroll in this course to access lessons and pay the fee.', { duration: 4000 });
                        setEnrollModalOpen(true);
                        return;
                      }
                      navigate(`/courses/${programSlug}/courses/${courseSlug}/sections/${section.id}`);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (canEditCourse) {
                          navigate(`/courses/${programSlug}/courses/${courseSlug}/sections/${section.id}`);
                          return;
                        }
                        if (!user) {
                          toast.info('Sign in to access lessons and track your progress', { duration: 4000 });
                          if (typeof window !== 'undefined' && course) {
                            window.sessionStorage.setItem(PENDING_ENROLL_KEY, course.id);
                          }
                          login();
                          return;
                        }
                        if (!enrollmentStatus?.enrolled) {
                          toast.info('Enroll in this course to access lessons and pay the fee.', { duration: 4000 });
                          setEnrollModalOpen(true);
                          return;
                        }
                        navigate(`/courses/${programSlug}/courses/${courseSlug}/sections/${section.id}`);
                      }
                    }}
                    className="group relative rounded-2xl border border-border bg-card overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <span className={`flex items-center justify-center w-12 h-12 rounded-xl bg-muted/80 ${iconColorClass} group-hover:opacity-90 transition-opacity`}>
                          <ModuleIcon className="w-6 h-6" />
                        </span>
                        {canEditCourse ? (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setEditingSection(section); setModulePanelOpen(true); }}
                              className="p-2 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"
                              aria-label="Edit module"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSectionToDelete(section);
                              }}
                              className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                              aria-label="Delete module"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${badges.lessons}`}>
                            {lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
                          </span>
                        )}
                      </div>
                      <h3 className="font-display font-bold text-lg text-foreground mb-2 group-hover:text-accent transition-colors">{section.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{section.description || '—'}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full border ${badges.lessons}`}>
                          <BookOpen className="w-3 h-3" /> {lessonCount} lessons
                        </span>
                        {section.totalDuration && (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full border ${badges.duration}`}>
                            <Clock className="w-3 h-3" /> {section.totalDuration}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon={BookOpen}
              title="No modules yet"
              message={canEditCourse ? 'Add a module to get started.' : 'Check back later or enroll to get updates.'}
            />
          )}
        </div>
      </section>

      <SlideOver open={modulePanelOpen} onClose={() => { setModulePanelOpen(false); setEditingSection(null); }} title={editingSection ? 'Edit module' : 'Add module'}>
        <ModuleForm
          initial={editingSection}
          onSave={async (payload) => {
            if (!programSlug || !courseSlug) return;
            try {
              if (editingSection) {
                await updateSection(programSlug, courseSlug, editingSection.id, payload as UpdateSectionPayload);
                toast.success('Module updated');
              } else {
                await createSection(programSlug, courseSlug, payload as CreateSectionPayload);
                toast.success('Module created');
              }
              setModulePanelOpen(false);
              setEditingSection(null);
              refetchCourse();
            } catch (err) {
              toast.error(err instanceof Error ? err.message : 'Failed to save module');
              throw err;
            }
          }}
          onCancel={() => { setModulePanelOpen(false); setEditingSection(null); }}
        />
      </SlideOver>

      <ConfirmDialog
        open={!!sectionToDelete}
        onClose={() => setSectionToDelete(null)}
        title="Delete module"
        description={sectionToDelete ? `Delete "${sectionToDelete.title}"? This will remove the module and all lessons in it. This cannot be undone.` : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteLoading}
        onConfirm={async () => {
          if (!sectionToDelete || !programSlug || !courseSlug) return;
          setDeleteLoading(true);
          try {
            await deleteSection(programSlug, courseSlug, sectionToDelete.id);
            refetchCourse();
          } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to delete module');
            throw err;
          } finally {
            setDeleteLoading(false);
          }
        }}
      />

      <EnrollModal
        open={enrollModalOpen}
        onClose={() => setEnrollModalOpen(false)}
        courseTitle={course.title}
        courseId={course.id}
        priceKes={course.price_kes}
        isAuthenticated={!!user}
        onSignIn={login}
        userEmail={user?.email}
        userName={user?.email?.split('@')[0]}
        getLearnerId={getLearnerId}
        onEnrollmentConfirmed={refetchEnrollment}
        onStartLearning={() => {
          refetchEnrollment().then(() => {
            if (course.sections && course.sections.length > 0) {
              navigate(`/courses/${programSlug}/courses/${courseSlug}/sections/${course.sections[0].id}`);
            }
          });
        }}
      />
    </div>
  );
}
